import { useEffect, useRef, useState } from "react";
import { useFull } from "../hooks/fullscreen";
import { isResponsiveView } from "../features/responsive";
import { getDomainCfg, store } from "../core/state";
import { getViewData } from "../core/views";
import { authenticateUser } from "../features/chatrequests";
import { CLOUD_USER } from "../features/cloudstorage/cloud";
import { showLoginRegisterModal } from "./modals/LoginRegisterModal";

export function AppIFrame(props: { doc: string, classNames: string, fullScreen: boolean, landscape: boolean, muteSound: boolean, hasAppBorder: boolean, timeStamp: number, highlight?: boolean }) {
  const frameRef = useRef(null)
  const containerRef = useRef(null); // Ref für den Container
  const [full, setFull] = useFull(frameRef);
  const [isHighlighted, setIsHighlighted] = useState(false);

  // MultiFile Service Logic
  let isMultiFileService = false;
  let multiFilePayload = null;
  let actualSrcDoc = props.doc;

  try {
    if (props.doc && props.doc.trim().startsWith('{"type":"multifile-service"')) {
      const parsed = JSON.parse(props.doc);
      if (parsed.type === 'multifile-service') {
        isMultiFileService = true;
        multiFilePayload = parsed.payload;
        actualSrcDoc = undefined; // Do not use srcDoc
      }
    }
  } catch (e) { /* ignore parse error */ }

  useEffect(() => {
    if (!isMultiFileService || !multiFilePayload) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'FRAME_LOADED') {
        if (frameRef.current && frameRef.current.contentWindow) {
          const sessionId = `tmp-${props.timeStamp}`;
          console.log("AppIFrame: sending PROJECT_DATA to service-app iframe", sessionId);
          frameRef.current.contentWindow.postMessage({
            type: 'PROJECT_DATA',
            sessionId: sessionId,
            payload: multiFilePayload
          }, '*');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isMultiFileService, multiFilePayload, props.timeStamp]);

  const vaibLoginShown = useRef(false);
  const [cloudUser] = store.useState(CLOUD_USER);

  const sendVaibAuth = async () => {
    const cfg = getDomainCfg();
    if (!cfg.chatSettings?.vaibSupport) return;

    try {
      const result = await authenticateUser();
      const token = localStorage.getItem('sessionToken');
      const chatUrl = cfg.chatSettings?.chatUrl;
      const serverUrl = cfg.serverSettings?.baseUrl;

      if (token && chatUrl && frameRef.current?.contentWindow) {
        frameRef.current.contentWindow.postMessage({
          type: 'VAIB_AUTH',
          token,
          chatUrl,
          serverUrl
        }, '*');
        vaibLoginShown.current = false;
        return;
      }

      if (result?.error || !token) {
        if (frameRef.current?.contentWindow) {
          frameRef.current.contentWindow.postMessage({
            type: 'VAIB_AUTH_ERROR',
            message: 'Nicht eingeloggt. Bitte zuerst anmelden.'
          }, '*');
        }
        if (!vaibLoginShown.current) {
          vaibLoginShown.current = true;
          showLoginRegisterModal(true);
        }
      }
    } catch {
      if (frameRef.current?.contentWindow) {
        frameRef.current.contentWindow.postMessage({
          type: 'VAIB_AUTH_ERROR',
          message: 'Authentifizierung fehlgeschlagen.'
        }, '*');
      }
      if (!vaibLoginShown.current) {
        vaibLoginShown.current = true;
        showLoginRegisterModal(true);
      }
    }
  };

  // VAIB Auth: Bei VAIB_AUTH_REQUEST vom Iframe Token validieren/erneuern und senden
  useEffect(() => {
    const handleVaibAuth = (event: MessageEvent) => {
      if (event.data?.type !== 'VAIB_AUTH_REQUEST') return;
      sendVaibAuth();
    };
    window.addEventListener('message', handleVaibAuth);
    return () => window.removeEventListener('message', handleVaibAuth);
  }, []);

  // Nach erfolgreichem Login: Token proaktiv senden
  useEffect(() => {
    if (cloudUser?.loggedIn && vaibLoginShown.current) {
      sendVaibAuth();
    }
  }, [cloudUser?.loggedIn]);

  useEffect(() => {
    if (props.highlight && !isHighlighted) {
      setIsHighlighted(true);
      setTimeout(() => setIsHighlighted(false), 800); // Setzt den Highlight-Status nach 1.25 Sekunden zurück

      // Scrollen zum iframecontainer, wenn im responsiven Modus
      if (isResponsiveView() && containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [props.highlight, isHighlighted]);

  if (props.fullScreen && !full) {
    setFull(true);
  } else if (!props.fullScreen && full) {
    setFull(false);
  }

  handleOrientation(props.landscape);

  const containerId = props.hasAppBorder ? "iframecontainer" : "iframecontainer2";

  const popupLangs = ['html', 'php'];
  const projectLang = getViewData('editor').project?.language;
  const allowPopups = popupLangs.includes(projectLang) ? "allow-popups" : "";
  let sandboxAttr = `allow-scripts allow-forms allow-modals allow-same-origin ${allowPopups}`;

  // Determine the classes for the wrapper
  const wrapperClasses = props.hasAppBorder ? props.classNames : 'full-height-wrapper';

  const isEmptyDoc = (d: string) => {
    if (d === undefined || d === null) return true as any;
    return String(d).trim().length === 0;
  };

  const defaultSrcDoc = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body { height: 100%; margin: 0; }
      body { display: flex; align-items: center; justify-content: center; font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif; background: #ffffff; color: #4b5563; }
      .wrap { text-align: center; }
      .play { width: 72px; height: 72px; border-radius: 50%; background: #e5e7eb; display: inline-flex; align-items: center; justify-content: center; margin: 8px auto 12px auto; }
      .play:before { content: ''; display: block; width: 0; height: 0; border-top: 10px solid transparent; border-bottom: 10px solid transparent; border-left: 16px solid #9ca3af; margin-left: 3px; }
      .text { font-family: monospace; }
    </style>
  </head>
  <body>
    <div class="wrap">
      <div class="play"></div>
      <div class="text">Klicke auf "Starten", um die Ausgabe hier zu sehen</div>
    </div>
  </body>
  </html>`;

  const srcDoc = isEmptyDoc(props.doc) ? defaultSrcDoc : actualSrcDoc;

  const iframeProps: any = {
    id: "iframeResult",
    key: props.timeStamp,
    ref: frameRef,
    title: "iframe sandbox",
    sandbox: sandboxAttr
  };

  if (isMultiFileService) {
    iframeProps.src = `/assets/templates/service-app/service-app.html?sessionId=tmp-${props.timeStamp}`;
    // allow-same-origin is critical for service workers, we make sure it's in sandboxAttr
    if (!iframeProps.sandbox.includes('allow-same-origin')) {
      iframeProps.sandbox += ' allow-same-origin';
    }
  } else {
    iframeProps.srcDoc = srcDoc;
  }

  return (
    <div id={containerId} ref={containerRef} className={`${isHighlighted ? 'highlighted-background' : ''} ${!props.hasAppBorder ? 'full-height-container' : ''}`}>
      <div id="iframewrapper" className={wrapperClasses}>
        <iframe {...iframeProps} />
      </div>
    </div>
  );
}

function handleOrientation(landscape: boolean) {
  const screen = (window as any).screen;
  if (!screen || !screen.orientation || screen.orientation.type === "landscape-primary") return;
  try {
    if (landscape) {
      screen.orientation.lock('landscape');
    } else {
      screen.orientation.lock('any');
    }
  } catch (err) {
    console.log("screen lock not possible!");
  }
}
